import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { Popover } from "./Popover";

/**
 * Beyond Sure — Menu primitive (A1, portal).
 *
 * A keyboard-navigable dropdown menu built on Popover. Use for:
 *   - The composer model picker (Auto ▾)
 *   - Per-row action menus on policy/family cards
 *   - The account dropdown (in lieu of ProfileMenu's inline impl)
 *   - Export / share menus on OCR Pro
 *
 * Items + sections are declared as children. Keyboard:
 *   ↑/↓     move focus
 *   Home/End jump first/last
 *   Enter   activate the focused item
 *   Esc     close, return focus to trigger
 *
 * Accessibility:
 *   role="menu" on the panel, role="menuitem" on items
 *   aria-activedescendant tracks the highlighted item
 *   disabled items skip in the keyboard cycle
 *
 * The Menu manages its own open state by default. Pass `open` +
 * `onOpenChange` for parent-controlled state.
 */

export interface MenuItemProps {
  id: string;
  /** Visible label. */
  children: ReactNode;
  /** Leading icon node. */
  icon?: ReactNode;
  /** Keyboard shortcut hint shown right-aligned (e.g. "⌘K"). */
  kbd?: string;
  /** Click handler. Called with no args. */
  onSelect?: () => void;
  /** Disabled — not focusable, dimmed. */
  disabled?: boolean;
  /** Checked state — shows a ✓ leading icon. */
  checked?: boolean;
  /** Destructive item — colored red. */
  destructive?: boolean;
  /** data-testid forwarded. */
  testid?: string;
}

interface MenuSectionProps {
  label?: string;
  children: ReactNode;
}

interface MenuProps {
  /** The trigger (button) — same render-prop pattern as Popover. */
  children:
    | ReactElement
    | ((props: {
        ref: (node: HTMLElement | null) => void;
        onClick: () => void;
        "aria-expanded": boolean;
        /** Inherited union from Popover's TriggerProps. Pass "menu" when rendering. */
        "aria-haspopup": "dialog" | "menu" | "listbox" | "true";
        "aria-controls": string;
      }) => ReactNode);
  /** Menu items + sections. */
  items: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
  /** Optional width — default fits content with a min. */
  minWidth?: number;
  /** ARIA label for the menu. */
  ariaLabel?: string;
}

export function Menu({
  children,
  items,
  side = "bottom",
  align = "start",
  open: controlledOpen,
  onOpenChange,
  minWidth = 200,
  ariaLabel,
}: MenuProps) {
  const isControlled = typeof controlledOpen === "boolean";
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) onOpenChange?.(next);
      else {
        setInternalOpen(next);
        onOpenChange?.(next);
      }
    },
    [isControlled, onOpenChange],
  );

  /* ── Collect focusable item ids in render order ─────────────────── */
  const enabledIds: string[] = [];
  function collect(node: ReactNode): void {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) return;
      if (child.type === MenuItem) {
        const p = child.props as MenuItemProps;
        if (!p.disabled) enabledIds.push(p.id);
      } else if (child.type === MenuSection) {
        collect((child.props as MenuSectionProps).children);
      }
    });
  }
  collect(items);

  const [activeId, setActiveId] = useState<string | null>(enabledIds[0] ?? null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Reset active item to first when menu opens.
  useEffect(() => {
    if (open) setActiveId(enabledIds[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── Keyboard nav (captured at panel level) ─────────────────────── */
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (enabledIds.length === 0) return;
      const idx = activeId ? enabledIds.indexOf(activeId) : -1;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = idx < enabledIds.length - 1 ? idx + 1 : 0;
        setActiveId(enabledIds[next]);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = idx > 0 ? idx - 1 : enabledIds.length - 1;
        setActiveId(enabledIds[prev]);
      } else if (e.key === "Home") {
        e.preventDefault();
        setActiveId(enabledIds[0]);
      } else if (e.key === "End") {
        e.preventDefault();
        setActiveId(enabledIds[enabledIds.length - 1]);
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (!activeId) return;
        // Walk children to find onSelect for activeId.
        const found = findItem(items, activeId);
        found?.onSelect?.();
        setOpen(false);
      }
    },
    [activeId, enabledIds, items, setOpen],
  );

  /* ── Inject activeId + setActiveId into items ───────────────────── */
  const decoratedItems = decorate(items, activeId, setActiveId, () => setOpen(false));

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side={side}
      align={align}
      role="menu"
      content={
        <div
          ref={panelRef}
          role="menu"
          aria-label={ariaLabel}
          aria-activedescendant={activeId ?? undefined}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          style={{ minWidth }}
          className="py-1 max-h-[60vh] overflow-y-auto focus:outline-none"
        >
          {decoratedItems}
        </div>
      }
    >
      {children}
    </Popover>
  );
}

/* ────────────────── MenuItem ────────────────── */

export function MenuItem(props: MenuItemProps & {
  __active?: boolean;
  __setActive?: (id: string) => void;
  __close?: () => void;
}) {
  const {
    id,
    children,
    icon,
    kbd,
    onSelect,
    disabled,
    checked,
    destructive,
    testid,
    __active,
    __setActive,
    __close,
  } = props;

  const handleClick = () => {
    if (disabled) return;
    onSelect?.();
    __close?.();
  };

  return (
    <div
      id={id}
      role="menuitem"
      aria-disabled={disabled}
      data-testid={testid}
      onMouseEnter={() => !disabled && __setActive?.(id)}
      onClick={handleClick}
      className={[
        "flex items-center gap-2.5 px-3 py-2 cursor-pointer select-none",
        "text-[13.5px] leading-[1.4]",
        disabled
          ? "opacity-40 cursor-not-allowed"
          : __active
          ? destructive
            ? "bg-[rgb(214_64_64_/_0.08)] text-[#9a3a3a]"
            : "bg-[var(--color-brand-soft)] text-[var(--color-ink)]"
          : destructive
          ? "text-[#9a3a3a] hover:bg-[rgb(214_64_64_/_0.06)]"
          : "text-[var(--color-ink)] hover:bg-[var(--color-ink-04)]",
      ].join(" ")}
    >
      {checked ? (
        <span className="w-3.5 text-[var(--color-brand-deep)] shrink-0" aria-hidden>
          ✓
        </span>
      ) : icon ? (
        <span className="w-3.5 text-[var(--color-ink-60)] shrink-0" aria-hidden>
          {icon}
        </span>
      ) : (
        <span className="w-3.5 shrink-0" />
      )}
      <span className="flex-1 min-w-0 truncate">{children}</span>
      {kbd ? (
        <kbd className="text-[10.5px] text-[var(--color-ink-40)] font-mono shrink-0">
          {kbd}
        </kbd>
      ) : null}
    </div>
  );
}

/* ────────────────── MenuSection ────────────────── */

export function MenuSection({ label, children }: MenuSectionProps) {
  return (
    <div role="group" aria-label={label}>
      {label ? (
        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-ink-40)]">
          {label}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/* ────────────────── MenuSeparator ────────────────── */

export function MenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-[var(--color-ink-08)]" />;
}

/* ────────────────── Helpers ────────────────── */

function decorate(
  node: ReactNode,
  activeId: string | null,
  setActiveId: (id: string) => void,
  close: () => void,
): ReactNode {
  return Children.map(node, (child) => {
    if (!isValidElement(child)) return child;
    if (child.type === MenuItem) {
      const props = child.props as MenuItemProps;
      return cloneElement(
        child as ReactElement<Record<string, unknown>>,
        {
          __active: activeId === props.id,
          __setActive: setActiveId,
          __close: close,
        } as unknown as Record<string, unknown>,
      );
    }
    if (child.type === MenuSection) {
      const sec = child.props as MenuSectionProps;
      return cloneElement(
        child as ReactElement<Record<string, unknown>>,
        {
          children: decorate(sec.children, activeId, setActiveId, close),
        } as unknown as Record<string, unknown>,
      );
    }
    return child;
  });
}

function findItem(node: ReactNode, id: string): MenuItemProps | null {
  let found: MenuItemProps | null = null;
  Children.forEach(node, (child) => {
    if (found) return;
    if (!isValidElement(child)) return;
    if (child.type === MenuItem) {
      const p = child.props as MenuItemProps;
      if (p.id === id) found = p;
    } else if (child.type === MenuSection) {
      found = findItem((child.props as MenuSectionProps).children, id);
    }
  });
  return found;
}

export default Menu;
